import { StripePaymentElementOptions } from '@stripe/stripe-js';
import * as types from '../types';

// @stripe/stripe-js doesn't export ApplePayOption from its public entrypoint, so derive it
// from the PaymentElement options type (the same shape ExpressCheckoutElement accepts).
export type ApplePayOption = NonNullable<StripePaymentElementOptions['applePay']>;

/**
 * Names what the sheet is for when the cart charges something today as well: the option carries no
 * line item for the tracker, so the description is where "tracker and membership" is said.
 */
export interface ApplePaySheetDisclosure {
  paymentDescription: string;
}

// the intro term is priced in whole months, so its end is the start moved that many months on
function addMonths(date: Date, months: number): Date {
  const end = new Date(date);
  end.setUTCMonth(end.getUTCMonth() + months);
  return end;
}

/**
 * The hardware-first membership charge the wallet sheet discloses. A priced trial wins, so a cart
 * holding a trial and a bought unit shows the trial's day-37 schedule, matching the sheet's
 * description; otherwise the server's charge, then the trial's.
 */
export function hardwareFirstChargeForSheet(
  trialActive: boolean,
  trialCharge: types.HardwareFirstRecurringCharge | undefined,
  serverCharge: types.HardwareFirstRecurringCharge | undefined,
): types.HardwareFirstRecurringCharge | undefined {
  return trialActive ? trialCharge : (serverCharge ?? trialCharge);
}

/**
 * The PaymentElement's apple pay options for a cart. A trial cart whose terms are not priced yet
 * gets no apple pay: the sheet would open without the tracker's day-30 charge, and it must never
 * fall back to the non-trial schedule. Card checkout is unaffected.
 */
export function applePayWalletOptions(
  trialPending: boolean,
  applePayRecurring: ApplePayOption | undefined,
): { wallets: { applePay: 'never' } } | { applePay: ApplePayOption } | Record<string, never> {
  if (trialPending) {
    return { wallets: { applePay: 'never' } };
  }
  return applePayRecurring ? { applePay: applePayRecurring } : {};
}

/**
 * Builds the Apple Pay recurringPaymentRequest option for Stripe Elements. Passing this to a
 * PaymentElement / ExpressCheckoutElement is what makes Stripe request an Apple Pay merchant
 * token (MPAN) rather than a single-use device token (DPAN). The MPAN is required to charge the
 * card off-session for recurring Fi memberships - without it renewals can fail.
 *
 * Returns undefined when the cart has no single recurring charge (e.g. one-time-only purchases),
 * in which case Apple Pay falls back to a standard one-time payment request.
 *
 * `disclosure` names the tracker in the description. Without it the sheet says only "Fi Membership",
 * which on a hardware-first cart reads as though the membership is the whole purchase.
 *
 * A hardware-first membership bills an intro price for its first term and the list price after, so
 * the intro term goes in `trialBilling` and `regularBilling` carries the renewal, starting when the
 * intro term ends. Apple renders both lines; a single line at the intro price would promise it forever.
 */
export function buildApplePayRecurringOption(
  recurringCharges: types.RecurringCharge[] | undefined,
  hardwareFirstCharge?: types.HardwareFirstRecurringCharge,
  disclosure?: ApplePaySheetDisclosure,
): ApplePayOption | undefined {
  // a hardware-first cart holds no subscription line, so its membership charge is the one to
  // disclose and it wins over the cart's own recurring charges.
  const charge = hardwareFirstCharge ?? (recurringCharges?.length === 1 ? recurringCharges[0] : undefined);
  if (!charge) {
    return undefined;
  }

  // Apple Pay's interval units don't include 'week' - express weekly billing in days instead
  const interval = charge.interval === 'week' ? 'day' : charge.interval;
  const intervalCount = charge.interval === 'week' ? charge.intervalCount * 7 : charge.intervalCount;
  // hardware-first bills days after checkout rather than immediately, so the sheet states when
  const startDate = hardwareFirstCharge ? new Date(hardwareFirstCharge.recurringPaymentStartDate) : undefined;
  const renewalAmountInCents = hardwareFirstCharge?.renewalAmountInCents;
  const introTermEndsAt =
    startDate &&
    charge.interval === 'month' &&
    renewalAmountInCents !== undefined &&
    renewalAmountInCents !== charge.amountInCents
      ? addMonths(startDate, intervalCount)
      : undefined;

  return {
    recurringPaymentRequest: {
      paymentDescription: disclosure?.paymentDescription ?? 'Fi Membership',
      managementURL: `${window.location.origin}/manage`,
      ...(introTermEndsAt &&
        startDate && {
          trialBilling: {
            label: 'Fi Membership, first year',
            amount: charge.amountInCents,
            recurringPaymentIntervalUnit: interval,
            recurringPaymentIntervalCount: intervalCount,
            recurringPaymentStartDate: startDate,
            recurringPaymentEndDate: introTermEndsAt,
          },
        }),
      regularBilling: {
        label: 'Fi Membership',
        amount: introTermEndsAt && renewalAmountInCents !== undefined ? renewalAmountInCents : charge.amountInCents,
        recurringPaymentIntervalUnit: interval,
        recurringPaymentIntervalCount: intervalCount,
        ...(startDate && { recurringPaymentStartDate: introTermEndsAt ?? startDate }),
      },
    },
  };
}

/**
 * Same as buildApplePayRecurringOption but for an existing membership's renewal terms
 * (update/recover payment flows, where there's no cart). Returns undefined when the terms
 * are unknown - e.g. Recurly won't preview overridden/comped subscriptions - so Apple Pay
 * falls back to a standard (device token) request rather than disclosing a wrong amount.
 */
export function buildApplePayRenewalOption(renewal: {
  amountInCents?: number | null;
  renewalMonths?: number | null;
}): ApplePayOption | undefined {
  const { amountInCents, renewalMonths } = renewal;
  if (!amountInCents || amountInCents <= 0 || !renewalMonths || renewalMonths <= 0) {
    return undefined;
  }
  return buildApplePayRecurringOption([
    { amountInCents, interval: 'month', intervalCount: renewalMonths, quantity: 1 },
  ]);
}
